import { Link, useLocation } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { 
  LayoutDashboard, 
  Users, 
  BriefcaseIcon, 
  FileCheck, 
  Calendar, 
  Newspaper, 
  MessageSquare, 
  BarChart, 
  Settings, 
  LogOut,
  Home
} from "lucide-react";

interface SidebarProps {
  collapsed: boolean;
  isMobile?: boolean;
  closeMobileMenu?: () => void;
}

export default function DashboardSidebar({ collapsed, isMobile = false, closeMobileMenu }: SidebarProps) {
  const [location] = useLocation();
  const { user, logoutMutation } = useAuth();

  if (!user) return null;

  const handleLogout = () => {
    logoutMutation.mutate();
    if (closeMobileMenu) closeMobileMenu();
  };

  // Define navigation items based on user role
  const getNavigationItems = () => {
    const commonItems = [
      {
        name: "Dashboard",
        href: "/dashboard",
        icon: <LayoutDashboard className="h-5 w-5" />,
      }
    ];

    // Client navigation
    if (user.role === 'client') {
      return [
        ...commonItems,
        {
          name: "My Profile",
          href: "/dashboard/client/profile",
          icon: <Users className="h-5 w-5" />,
        },
        {
          name: "Job Opportunities",
          href: "/dashboard/client/jobs",
          icon: <BriefcaseIcon className="h-5 w-5" />,
        },
        {
          name: "My Applications",
          href: "/dashboard/client/applications",
          icon: <FileCheck className="h-5 w-5" />,
        },
        {
          name: "Appointments",
          href: "/dashboard/client/appointments",
          icon: <Calendar className="h-5 w-5" />,
        },
        {
          name: "Resources",
          href: "/dashboard/client/resources",
          icon: <Newspaper className="h-5 w-5" />,
        },
        {
          name: "Messages",
          href: "/dashboard/client/messages",
          icon: <MessageSquare className="h-5 w-5" />,
        },
      ];
    }

    // Case Worker navigation
    if (user.role === 'caseWorker') {
      return [
        ...commonItems,
        {
          name: "My Clients",
          href: "/dashboard/case-worker/clients",
          icon: <Users className="h-5 w-5" />,
        },
        {
          name: "Appointments",
          href: "/dashboard/case-worker/appointments",
          icon: <Calendar className="h-5 w-5" />,
        },
        {
          name: "Job Listings",
          href: "/dashboard/case-worker/jobs",
          icon: <BriefcaseIcon className="h-5 w-5" />,
        },
        {
          name: "Reports",
          href: "/dashboard/case-worker/reports",
          icon: <BarChart className="h-5 w-5" />,
        },
        {
          name: "Messages",
          href: "/dashboard/case-worker/messages",
          icon: <MessageSquare className="h-5 w-5" />,
        },
      ];
    }

    // Admin navigation
    if (user.role === 'admin') {
      return [
        ...commonItems,
        {
          name: "Users",
          href: "/dashboard/admin/users",
          icon: <Users className="h-5 w-5" />,
        },
        {
          name: "Case Workers",
          href: "/dashboard/admin/case-workers",
          icon: <Users className="h-5 w-5" />,
        },
        {
          name: "Employers",
          href: "/dashboard/admin/employers",
          icon: <BriefcaseIcon className="h-5 w-5" />,
        },
        {
          name: "Job Listings",
          href: "/dashboard/admin/jobs",
          icon: <BriefcaseIcon className="h-5 w-5" />,
        },
        {
          name: "Success Stories",
          href: "/dashboard/admin/success-stories",
          icon: <Newspaper className="h-5 w-5" />,
        },
        {
          name: "Reports",
          href: "/dashboard/admin/reports",
          icon: <BarChart className="h-5 w-5" />,
        },
        {
          name: "Settings",
          href: "/dashboard/admin/settings",
          icon: <Settings className="h-5 w-5" />,
        },
      ];
    }

    // Employer navigation
    if (user.role === 'employer') {
      return [
        ...commonItems,
        {
          name: "My Profile",
          href: "/dashboard/employer/profile",
          icon: <Users className="h-5 w-5" />,
        },
        {
          name: "Job Postings",
          href: "/dashboard/employer/jobs",
          icon: <BriefcaseIcon className="h-5 w-5" />,
        },
        {
          name: "Post a Job",
          href: "/dashboard/employer/post-job",
          icon: <FileCheck className="h-5 w-5" />,
        },
        {
          name: "Applications",
          href: "/dashboard/employer/applications",
          icon: <FileCheck className="h-5 w-5" />,
        },
        {
          name: "Candidates",
          href: "/dashboard/employer/candidates",
          icon: <Users className="h-5 w-5" />,
        },
        {
          name: "Messages",
          href: "/dashboard/employer/messages",
          icon: <MessageSquare className="h-5 w-5" />,
        },
      ];
    }

    return commonItems;
  };

  const navigationItems = getNavigationItems();

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Logo */}
      <div className={`p-4 flex items-center ${collapsed && !isMobile ? 'justify-center' : ''}`}>
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-full bg-primary-600 flex items-center justify-center text-white font-bold">
            SA
          </div>
          {(!collapsed || isMobile) && (
            <span className="text-xl font-bold text-primary-700">StaffAbility</span>
          )}
        </div>
      </div>

      <Separator />

      {/* Navigation Links */}
      <div className="flex-1 py-4 overflow-y-auto">
        <nav className="px-2 space-y-1">
          {navigationItems.map((item) => (
            <Link key={item.href} href={item.href}>
              <a
                className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                  location === item.href
                    ? "bg-primary-50 text-primary-700"
                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                } ${collapsed && !isMobile ? "justify-center" : ""}`}
                onClick={closeMobileMenu}
              >
                <div className={`${collapsed && !isMobile ? "mx-auto" : "mr-3"}`}>
                  {item.icon}
                </div>
                {(!collapsed || isMobile) && <span>{item.name}</span>}
              </a>
            </Link>
          ))}
        </nav>
      </div>

      {/* Bottom Actions */}
      <div className="p-4">
        <div className="space-y-2">
          <Link href="/">
            <a
              className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md text-slate-600 hover:bg-slate-50 hover:text-slate-900 ${
                collapsed && !isMobile ? "justify-center" : ""
              }`}
              onClick={closeMobileMenu}
            >
              <div className={`${collapsed && !isMobile ? "mx-auto" : "mr-3"}`}>
                <Home className="h-5 w-5" />
              </div>
              {(!collapsed || isMobile) && <span>Back to Website</span>}
            </a>
          </Link>
          
          <Button
            variant="ghost"
            className={`w-full ${collapsed && !isMobile ? "px-2 justify-center" : ""}`}
            onClick={handleLogout}
            disabled={logoutMutation.isPending}
          >
            <div className={`${collapsed && !isMobile ? "mx-auto" : "mr-2"}`}>
              <LogOut className="h-5 w-5" />
            </div>
            {(!collapsed || isMobile) && (
              <span>{logoutMutation.isPending ? "Logging out..." : "Logout"}</span>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
